import React, { useEffect, useRef, useState } from 'react';
import { siftApiClient } from '../../api/siftApiClient';
import type { BrokerBuyerMatch, BrokerBuyerMatchesResponse, BrokerBuyerSearchResult, BrokerEngagedBuyer } from '../../api/types';
import { fitScoreTooltip, splitScoreParts, BUYER_5MIL_HELP } from './brokerViewTokens';
import { DistanceTolerance } from './DistanceTolerance';
import './BuyerMatchPanel.css';
import { track } from '../../telemetry/usage';

// Carries the resolved ZIP/state so the basket shows where an engaged buyer is,
// same as a recommended one (§2.4).
const toBasketBuyer = (b: BrokerEngagedBuyer): BrokerBuyerMatch => ({
  buyer_uid: b.buyer_uid,
  buyer_name: b.buyer_name,
  buyer_zip_code: b.buyer_zip_code ?? null,
  state: b.state ?? null,
  distance_miles: b.distance_miles ?? null,
} as BrokerBuyerMatch);

interface BuyerMatchPanelProps {
  vin: string;
  /** How many ranked buyers to request (default 5 — the "exactly-right" set). */
  limit?: number;
  /** When provided, each buyer card shows an "Add to basket" action. */
  onAddToBasket?: (buyer: BrokerBuyerMatch, vin: string) => void;
  /** Stage several buyers in one write; "Add all" falls back to onAddToBasket per buyer. */
  onAddManyToBasket?: (buyers: BrokerBuyerMatch[], vin: string) => void;
  /** buyer_uid values already in the basket for this VIN (renders "Added"). */
  basketKeys?: Set<string>;
  /** Unstage a buyer for this VIN: clicking an added (✓) button undoes the add. */
  onRemoveFromBasket?: (buyerUid: string, vin: string) => void;
  /** §14.17: click a recommended buyer to jump to that buyer on the Buyer tab. */
  onFocusBuyer?: (buyerUid: string, buyerName?: string | null) => void;
}

// ---------- formatters ----------
const fmtMoney = (n?: number | null) =>
  n == null ? '–' : `$${Math.round(n).toLocaleString()}`;
const fmtInt = (n?: number | null) =>
  n == null ? '–' : Math.round(n).toLocaleString();
const fmtNum = (n?: number | null, digits = 1) =>
  n == null ? '–' : n.toFixed(digits);
const fmtMiles = (n?: number | null) =>
  n == null ? '—' : `${Math.round(n).toLocaleString()} mi`;

// Base pill style for the observed-engagement badges (color is applied per badge).
const engBadgeBase: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  padding: '1px 7px',
  borderRadius: 10,
  whiteSpace: 'nowrap',
};

/**
 * MMR retention is a RATIO — average sold price ÷ adjusted MMR (§0.6).
 * 1.0 = paid exactly MMR; >1 = above MMR; <1 = below. Shown as % of MMR.
 */
const fmtRetention = (n?: number | null) =>
  n == null ? '–' : `${Math.round(n * 100)}% of MMR`;

// Tier → chip color. Tighter match = warmer/greener (best fit).
const TIER_COLOR: Record<number, { bg: string; fg: string }> = {
  4: { bg: '#dcfce7', fg: '#15803d' }, // Exact
  3: { bg: '#dbeafe', fg: '#1d4ed8' }, // Cohort
  2: { bg: '#fef3c7', fg: '#a16207' }, // Similar
  1: { bg: '#f1f5f9', fg: '#475569' }, // Make/Model
  0: { bg: '#ede9fe', fg: '#6d28d9' }, // Segment (cross-model)
};

// §1.11: only badge buyers broad enough for it to be actionable. 26+ VINs is the
// top ~4.6% of the pool; below that the count is noise on every card.
const BROAD_BUYER_MIN_VINS = 25;

// §1.5: a single incidental previously-Canadian purchase is not an affinity. Only
// badge a buyer who has bought them repeatedly.
const PREV_CA_MIN_BUYS = 5;

// What each match tier means (trim + drivetrain are folded into "Exact").
const TIER_DESC: Record<number, string> = {
  4: 'Exact: same year / mileage / CR cohort + trim + drivetrain',
  3: 'Cohort: same year / mileage / CR band',
  2: 'Similar: same age + mileage or CR band',
  1: 'Make/model only (outside the year/mileage/CR cohort)',
  0: 'Segment: same body/size segment — a different make/model in the same class (incl. same-parent siblings, e.g. GMC ↔ Chevy)',
};

/**
 * Click-to-copy contact email. Rendered as an icon rather than the full address
 * because the buyer row is already dense; coverage is ~94%, so a missing chip
 * means "no email on file for this buyer", not "this buyer can't be emailed".
 */
/** Where an address came from, in words a broker can act on. The ladder decides
 *  WHICH address is offered, so hiding it leaves "why this one?" unanswerable. */
const EMAIL_SOURCE_LABEL: Record<string, string> = {
  purchase: 'bought from us at this address',
  bid_activity: 'used when bidding',
  engagement: 'used when browsing',
  contact_directory: 'from the contact directory',
  account_owner: 'account owner on file',
};

const BuyerEmailChip: React.FC<{ email?: string | null; source?: string | null }> = ({ email, source }) => {
  const [copied, setCopied] = useState(false);
  const addr = (email || '').trim();
  if (!addr) return null;
  const why = source ? EMAIL_SOURCE_LABEL[source] || source : null;
  const label = why ? `${addr} (${why}) — click to copy` : `${addr} — click to copy`;
  return (
    <button
      type="button"
      className="bmp-buyer-email"
      title={copied ? 'Copied' : label}
      aria-label={`Copy email ${addr}${why ? `, ${why}` : ''}`}
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(addr).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }).catch(() => { /* clipboard blocked — the address is in the tooltip */ });
      }}
    >
      {copied ? '✓' : '✉'}
    </button>
  );
};

/**
 * The +/✓ basket control. ✓ is a toggle when removal is wired: clicking it again
 * unstages this buyer for this VIN, so an accidental add is one click to undo.
 */
const BasketToggle: React.FC<{
  inBasket: boolean;
  onAdd: () => void;
  onRemove?: () => void;
  className?: string;
  addedClassName?: string;
  addLabel?: React.ReactNode;
  addedLabel?: React.ReactNode;
  /** Icon-only buttons need an aria-label; text buttons keep their visible text as the name. */
  iconOnly?: boolean;
  style?: React.CSSProperties;
}> = ({ inBasket, onAdd, onRemove, className = 'bmp-basket-btn', addedClassName = 'bmp-basket-btn--added',
        addLabel = '+', addedLabel = '✓', iconOnly = true, style }) => {
  const removable = inBasket && !!onRemove;
  const label = inBasket
    ? (removable ? 'In basket — click to remove' : 'In basket')
    : 'Add to outreach basket';
  return (
    <button
      type="button"
      className={`${className}${inBasket ? ` ${addedClassName}` : ''}`}
      onClick={(e) => {
        e.stopPropagation();
        if (!inBasket) onAdd();
        else if (onRemove) onRemove();
      }}
      disabled={inBasket && !onRemove}
      aria-pressed={inBasket}
      aria-label={iconOnly ? label : undefined}
      title={label}
      style={style}
    >
      {inBasket ? addedLabel : addLabel}
    </button>
  );
};

const BuyerCard: React.FC<{
  buyer: BrokerBuyerMatch;
  rank: number;
  vin: string;
  onAddToBasket?: (buyer: BrokerBuyerMatch, vin: string) => void;
  onRemoveFromBasket?: (buyerUid: string, vin: string) => void;
  inBasket: boolean;
  onFocusBuyer?: (buyerUid: string, buyerName?: string | null) => void;
}> = ({ buyer, rank, vin, onAddToBasket, onRemoveFromBasket, inBasket, onFocusBuyer }) => {
  const tierColor = TIER_COLOR[buyer.match_tier] ?? TIER_COLOR[1];
  const locationBits = [buyer.state, buyer.buyer_zip_code].filter(Boolean).join(' · ');
  const [showWhy, setShowWhy] = useState(false);
  const { components, adjustments, total } = splitScoreParts(buyer.score_parts, buyer.score);
  return (
    <div className="bmp-row" data-usage-buyer-uid={buyer.buyer_uid} data-usage-section="recommended_buyers">
      <div className="bmp-row-main">
        <span className="bmp-rank">#{rank}</span>
        <button
          type="button"
          className={`bmp-score${showWhy ? ' bmp-score--open' : ''}`}
          aria-expanded={showWhy}
          onClick={() => setShowWhy((v) => !v)}
          title={fitScoreTooltip(buyer.score, buyer.match_label, buyer.score_parts)}
        >
          Fit {Math.round(buyer.score)} <span className="bmp-score-why">why?</span>
        </button>
        {onFocusBuyer ? (
          <button
            type="button"
            className="bmp-buyer-name bmp-buyer-name--link"
            title={`Shop other vehicles for this buyer — open ${buyer.buyer_name || buyer.buyer_uid} on the Buyer tab`}
            onClick={() => onFocusBuyer(String(buyer.buyer_uid), buyer.buyer_name)}
          >
            {buyer.buyer_name || `Buyer ${buyer.buyer_uid}`}
          </button>
        ) : (
          <span className="bmp-buyer-name" title={locationBits}>{buyer.buyer_name || `Buyer ${buyer.buyer_uid}`}</span>
        )}
        <span className="bmp-buyer-id" title={BUYER_5MIL_HELP}>#{buyer.buyer_uid}</span>
        <BuyerEmailChip email={buyer.buyer_email} source={buyer.buyer_email_source} />
        {locationBits && (
          <span className="bmp-buyer-loc" title="Buyer location (state · ZIP)">{locationBits}</span>
        )}
        <span className="bmp-tier" style={{ background: tierColor.bg, color: tierColor.fg }} title={TIER_DESC[buyer.match_tier] ?? TIER_DESC[1]}>
          {buyer.match_label}
        </span>
        {buyer.distance_miles != null && (
          <span className="bmp-distance" title="Distance from the vehicle's proximity anchor">
            {fmtMiles(buyer.distance_miles)}
          </span>
        )}
        {buyer.board_breadth != null && buyer.board_breadth >= BROAD_BUYER_MIN_VINS && (
          <span
            style={{ ...engBadgeBase, background: '#fffbeb', color: '#92400e' }}
            title={`This buyer is a top-5 match on ${buyer.board_breadth.toLocaleString()} vehicles currently on the board. They earn this slot on fit — this is how widely they match, not how often they have been contacted. Worth pacing your outreach.`}
          >
            ↔ {buyer.board_breadth.toLocaleString()} VINs
          </span>
        )}
        {(buyer.prev_canadian_buys ?? 0) >= PREV_CA_MIN_BUYS && (
          <span
            style={{ ...engBadgeBase, background: '#ecfdf5', color: '#065f46' }}
            title={`Has bought ${buyer.prev_canadian_buys?.toLocaleString()} previously-Canadian units${
              buyer.prev_canadian_share != null
                ? ` (${(buyer.prev_canadian_share * 100).toFixed(1)}% of their purchases)`
                : ''
            }. This is what they BUY — not whether the buyer is Canadian.`}
          >
            Buys prev-CA ({buyer.prev_canadian_buys?.toLocaleString()})
          </span>
        )}
        {buyer.is_canadian && (
          <span
            style={{ ...engBadgeBase, background: '#fef2f2', color: '#b91c1c' }}
            title="Previous Canadian buyer (buyer country = Canada). Cross-border transport / import considerations may apply."
          >
            🇨🇦 CA
          </span>
        )}
        {onAddToBasket && (
          <BasketToggle
            inBasket={inBasket}
            onAdd={() => onAddToBasket(buyer, vin)}
            onRemove={onRemoveFromBasket ? () => onRemoveFromBasket(buyer.buyer_uid, vin) : undefined}
          />
        )}
      </div>
      {buyer.engagement &&
        (buyer.engagement.already_bidding || buyer.engagement.lost_bid || buyer.engagement.viewed_or_saved) && (
        <div className="bmp-row-engagement" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 2 }}>
          {buyer.engagement.already_bidding && (
            <span
              style={{ ...engBadgeBase, background: '#fee2e2', color: '#b91c1c' }}
              title="Has an active bid on this VIN — outreach may undercut an active bid"
            >
              ● Bidding now{buyer.engagement.bid_count ? ` (${buyer.engagement.bid_count})` : ''}
            </span>
          )}
          {!buyer.engagement.already_bidding && buyer.engagement.lost_bid && (
            <span
              style={{ ...engBadgeBase, background: '#dbeafe', color: '#1d4ed8' }}
              title="Bid on this VIN but lost / expired — a re-engagement target"
            >
              Lost bid — re-engage
            </span>
          )}
          {buyer.engagement.viewed_or_saved && (
            <span
              style={{ ...engBadgeBase, background: '#f1f5f9', color: '#475569' }}
              title="Viewed the VDP or saved this VIN to a workbook"
            >
              {buyer.engagement.workbook_saved ? 'Saved' : 'Viewed'}
              {buyer.engagement.vdp_views ? ` · ${buyer.engagement.vdp_views} views` : ''}
            </span>
          )}
        </div>
      )}
      <div className="bmp-row-stats">
        <span><b>{fmtInt(buyer.total_buys)}</b> buys</span>
        <span title="This buyer's historical average purchase price across all their buys — not a quote or estimate for this VIN."><b>{fmtMoney(buyer.avg_sold_price)}</b> avg buy</span>
        <span title="Avg sold price as a % of adjusted MMR (retention). 100% = paid exactly MMR; higher = paid above."><b>{fmtRetention(buyer.avg_sold_adj_mmr_ret)}</b></span>
        <span><b>{fmtInt(buyer.total_bids)}</b> bids</span>
        <span><b>{fmtNum(buyer.avg_cr_grade)}</b> CR</span>
        <span><b>{fmtInt(buyer.avg_mileage)}</b> mi</span>
      </div>
      {showWhy && (
        <div className="bmp-why" role="region" aria-label={`Fit breakdown for ${buyer.buyer_name || buyer.buyer_uid}`}>
          <div className="bmp-why-head">
            How this Fit was calculated
          </div>
          {components.map((p) => (
            <div key={p.label} className="bmp-why-row">
              <span className="bmp-why-label">{p.label}</span>
              <span className="bmp-why-detail">{p.detail}</span>
              <span className="bmp-why-bar" aria-hidden="true">
                <span
                  className="bmp-why-fill"
                  style={{ width: `${Math.max(0, Math.min(100, (p.points / (p.max_points || 1)) * 100))}%` }}
                />
              </span>
              <span className="bmp-why-pts">{p.display.toFixed(1)}<em> / {p.max_points}</em></span>
            </div>
          ))}
          {adjustments.map((p) => (
            <div key={p.label} className="bmp-why-row bmp-why-row--adj">
              <span className="bmp-why-label">{p.label}</span>
              <span className="bmp-why-detail">{p.detail}</span>
              <span className="bmp-why-bar" aria-hidden="true" />
              <span className={`bmp-why-pts${p.display < 0 ? ' is-neg' : ' is-pos'}`}>
                {p.display >= 0 ? '+' : ''}{p.display.toFixed(1)}
              </span>
            </div>
          ))}
          <div className="bmp-why-row bmp-why-total">
            <span className="bmp-why-label">Fit</span>
            <span className="bmp-why-detail">
              {adjustments.length ? 'components + adjustments' : 'sum of components'}
            </span>
            <span className="bmp-why-bar" aria-hidden="true" />
            <span className="bmp-why-pts">{total.toFixed(1)}</span>
          </div>
          <div className="bmp-why-note">
            The first six are weighted components of the 0–100 blend, each shown against
            the most it can contribute. Anything below them is a read-time adjustment
            applied on top, and every weight is set in Admin → Broker settings.
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Ranked buyer recommendations for one stalled VIN. Fetches
 * `GET /broker-view/vins/{vin}/buyers` and renders the ~5 exactly-right buyers
 * (match tier, proximity, buyer stats, MMR retention). Self-contained: pass a
 * `vin` and it handles loading / empty / error states.
 */
export const BuyerMatchPanel: React.FC<BuyerMatchPanelProps> = ({
  vin,
  limit = 5,
  onAddToBasket,
  onAddManyToBasket,
  basketKeys,
  onRemoveFromBasket,
  onFocusBuyer,
}) => {
  const [data, setData] = useState<BrokerBuyerMatchesResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // §10-H "see beyond the top 5": brokers can opt in to a deeper slice of the
  // ranked pool. Default is the passed `limit` (5); expanding refetches at the
  // number of buyers that ACTUALLY survive the filters, capped by the route's max.
  // §5.3: this used to ask for a flat 25 and got a median of 10, so the control
  // advertised buyers that could not exist.
  const MAX_LIMIT = 25;
  const [viewLimit, setViewLimit] = useState(limit);
  // §1.3 distance tolerance, TRANSIENT for this panel only -- undefined means "use
  // the configured default". Nothing is persisted, so a reload returns to the config.
  const [tolerance, setTolerance] = useState<number | undefined>(undefined);
  // Trails `tolerance` by a beat so sweeping the slider is one query, not forty.
  const [debouncedTolerance, setDebouncedTolerance] = useState<number | undefined>(undefined);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (tolerance === debouncedTolerance) return;
    const t = setTimeout(() => setDebouncedTolerance(tolerance), 200);
    return () => clearTimeout(t);
  }, [tolerance, debouncedTolerance]);

  // Collapse back to the default whenever the VIN (or the caller's cap) changes.
  useEffect(() => {
    setViewLimit(limit);
    setTolerance(undefined);
    setDebouncedTolerance(undefined);
  }, [vin, limit]);

  useEffect(() => {
    if (!vin) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await siftApiClient.getBrokerViewVinBuyers(vin, viewLimit, controller.signal, debouncedTolerance);
        if (cancelled) return;
        setData(res);
      } catch (err: any) {
        if (controller.signal.aborted || err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
        if (cancelled) return;
        setError('Could not load buyer recommendations.');
        setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [vin, viewLimit, debouncedTolerance]);

  const veh = data?.vehicle;
  const cohortBits = veh
    ? [veh.cohort_year, veh.cohort_mileage ? `${veh.cohort_mileage} mi` : null,
       veh.cohort_condition ? `CR ${veh.cohort_condition}` : null].filter(Boolean).join(' · ')
    : '';

  // Buyer totals ("120 buys", "300 bids") cover a rolling window, not the
  // buyer's lifetime — say so, or a low count reads as an inactive buyer.
  const win = data?.buyer_history_window;
  const historyNote = win?.activity_days
    ? {
        label: `Buy/bid history covers the last ${win.activity_days} days`
          + (win.engagement_days ? ` · this-VIN engagement ${win.engagement_days} days` : ''),
        detail: [
          win.activity_start && win.activity_end
            ? `Purchase and bid totals are for ${win.activity_start} to ${win.activity_end}, not lifetime.`
            : null,
          win.engagement_start && win.engagement_end
            ? `Engagement on this listing covers ${win.engagement_start} to ${win.engagement_end}.`
            : null,
          'A buyer with few buys in this window is not necessarily inactive.',
        ].filter(Boolean).join(' '),
      }
    : null;

  return (
    <section className="bmp-panel">
      <div className="bmp-header">
        <h3 className="bmp-title" title="The ~5 buyers most likely to move this VIN — ranked by cohort match, proximity, buyer strength, MMR retention, recency and engagement. Existing bidders are surfaced too; verify announcements before reaching out.">
          Recommended Buyers
        </h3>
        {data && data.total_candidates > 0 && (
          <span className="bmp-count">
            Top {data.returned} of {data.total_candidates.toLocaleString()}
          </span>
        )}
      </div>

      {cohortBits && (
        <div className="bmp-cohort" title="Matched vehicle cohort">
          Cohort: {cohortBits}
        </div>
      )}

      {historyNote && (
        <div className="bmp-history-note" title={historyNote.detail}>
          {historyNote.label}
        </div>
      )}

      {veh && (veh.vehicle_type || veh.trim_level != null) && (
        <div
          className="bmp-cohort"
          title="Competitive segment + trim rank (1 = top trim … 6 = base). Used for cross-model / Segment matching."
        >
          {[veh.vehicle_type, veh.trim_level != null ? `Trim level ${veh.trim_level}/6` : null]
            .filter(Boolean)
            .join(' · ')}
        </div>
      )}

      {loading && <div className="bmp-state">Finding the right buyers…</div>}
      {!loading && error && <div className="bmp-state bmp-state--error">{error}</div>}
      {/* The cohort wording is only true when distance isn't the reason; the
          distance note below explains the empty list when it is. */}
      {!loading && !error && data && data.buyers.length === 0
        && data.match_state !== 'distance_limited' && data.match_state !== 'no_history' && (
        <div className="bmp-state">
          {data.distance_search === 'within' && data.distance_cap_miles
            ? `No matched buyers within ${Math.round(data.distance_cap_miles).toLocaleString()} mi.`
            : "No matching buyers found for this vehicle's cohort."}
        </div>
      )}
      {!loading && !error && data && data.distance_search === 'within' && data.buyers.length > 0
        && data.distance_cap_miles != null && (
        <div
          className="bmp-history-note"
          title="With a typed distance, the list is re-ranked among only the buyers inside it, so the closest qualifying buyers appear even if they weren't in the default top results."
        >
          Best matches within {Math.round(data.distance_cap_miles).toLocaleString()} mi
        </div>
      )}

      {/* §1.6 say WHY the list is short. "Insufficient history" is only true for the
          1.28% with no cohort match; 21.63% are short purely because of the cap.
          match_state already implies a short list, so it is the only guard needed. */}
      {!loading && !error && data && data.match_state === 'no_history' && (
        <div className="bmp-thin bmp-thin--history">
          <b>Insufficient history</b> — no past buyers match this vehicle's cohort, so no fit
          score can be produced.
          {(data.other_engaged_buyers?.length ?? 0) > 0
            ? ' Buyers who engaged this listing are shown below.'
            : ''}
        </div>
      )}
      {!loading && !error && data && data.match_state === 'distance_limited' && (
        <div className="bmp-thin bmp-thin--distance">
          <b>Limited by distance, not history</b> — {data.buyers_beyond_cap?.toLocaleString()} matched
          {data.buyers_beyond_cap === 1 ? ' buyer sits' : ' buyers sit'} beyond the{' '}
          {Math.round(data.effective_distance_cap_miles ?? 0).toLocaleString()} mi range
          {data.nearest_buyer_beyond_cap_miles != null && (
            <>, the nearest at {Math.round(data.nearest_buyer_beyond_cap_miles).toLocaleString()} mi</>
          )}
          . Widen the distance tolerance to reach them.
        </div>
      )}

      {!loading && !error && data && data.buyers.length > 0 && (
        <div className="bmp-list">
          {data.buyers.map((b, i) => (
            <BuyerCard
              key={b.buyer_uid || i}
              buyer={b}
              rank={i + 1}
              vin={data.vin}
              onAddToBasket={onAddToBasket}
              onRemoveFromBasket={onRemoveFromBasket}
              inBasket={!!basketKeys?.has(b.buyer_uid)}
              onFocusBuyer={onFocusBuyer}
            />
          ))}
        </div>
      )}

      {/* §10-H see-beyond-5: opt into a deeper slice of the ranked pool, or collapse back. */}
      {!loading && !error && data && data.buyers.length > 0 && (
        (viewLimit <= limit && data.total_candidates > data.buyers.length) ? (
          <button
            type="button"
            className="bmp-see-more"
            onClick={() => setViewLimit(Math.min(data.total_candidates, MAX_LIMIT))}
            style={{ margin: '8px 0 0', background: 'transparent', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
            title="Reach deeper into the ranked buyer pool beyond the top 5"
          >
            See all {Math.min(data.total_candidates, MAX_LIMIT)} buyers ▾
          </button>
        ) : viewLimit > limit ? (
          <button
            type="button"
            className="bmp-see-more"
            onClick={() => setViewLimit(limit)}
            style={{ margin: '8px 0 0', background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
            title="Collapse back to the top 5"
          >
            Show top {limit} ▴
          </button>
        ) : null
      )}

      {/* §1.3 transient distance tolerance. The distance cap is why the list is short
          (median 10 of 25 survive it), so this is the real "see more buyers" lever.
          Session-only: changing the VIN or reloading returns to the configured default. */}
      {!loading && !error && data && (
        <div className="bmp-tolerance" style={{ margin: '8px 0 0' }}>
          <DistanceTolerance
            id="bmp-tolerance-slider"
            label="Distance"
            value={tolerance}
            // The default cap is value-scaled per vehicle; show the one in force.
            fallbackMiles={data.effective_distance_cap_miles ?? data.distance_cap_miles}
            onChange={setTolerance}
            onReset={() => setTolerance(undefined)}
            title="Widen or tighten how far a buyer can be for THIS vehicle. Not saved."
          />
        </div>
      )}

      {!loading && !error && data && (data.other_engaged_buyers?.length ?? 0) > 0 && (() => {
        const engaged = data.other_engaged_buyers!;
        const allStaged = engaged.every((b) => !!basketKeys?.has(b.buyer_uid));
        const addAll = () => {
          const pending = engaged.filter((b) => !basketKeys?.has(b.buyer_uid)).map(toBasketBuyer);
          if (pending.length === 0) return;
          track('broker_view', 'basket', 'add_all_engaged', { vin }, { count: pending.length });
          if (onAddManyToBasket) onAddManyToBasket(pending, vin);
          else pending.forEach((b) => onAddToBasket?.(b, vin));
        };
        const removeAll = () => engaged.forEach((b) => onRemoveFromBasket?.(b.buyer_uid, vin));
        return (
        <div className="bmp-engaged" style={{ marginTop: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, margin: '0 0 6px' }}>
            <div
              className="bmp-engaged-title"
              style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}
              title="Buyers who bid on, viewed, or saved THIS VIN but aren't top cohort matches — prime re-engagement targets, especially lost bidders."
            >
              Also engaged this VIN
            </div>
            {onAddToBasket && (allStaged ? (
              onRemoveFromBasket && (
                <button
                  type="button"
                  className="bmp-engaged-all"
                  onClick={removeAll}
                  style={{ background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                  title="Take every buyer in this list back out of the outreach basket for this VIN"
                >
                  Remove all
                </button>
              )
            ) : (
              <button
                type="button"
                className="bmp-engaged-all"
                onClick={addAll}
                style={{ background: 'transparent', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                title="Stage every buyer in this list in the outreach basket for this VIN"
              >
                Add all ({engaged.length}) to basket
              </button>
            ))}
          </div>
          <div className="bmp-list">
            {engaged.map((b) => {
              const e = b.engagement;
              const loc = [
                b.buyer_country && b.buyer_country !== 'US' ? b.buyer_country : null,
                b.buyer_parent_name,
              ].filter(Boolean).join(' · ');
              const where = [b.state, b.buyer_zip_code].filter(Boolean).join(' · ');
              const inBasket = !!basketKeys?.has(b.buyer_uid);
              return (
                <div className="bmp-row bmp-row--engaged" key={b.buyer_uid}
                  data-usage-buyer-uid={b.buyer_uid} data-usage-section="engaged_buyers">
                  <div className="bmp-row-main" style={{ flexWrap: 'wrap', gap: 6 }}>
                    <span className="bmp-buyer-name" title={loc}>{b.buyer_name || `Buyer ${b.buyer_uid}`}</span>
                    <span className="bmp-buyer-id" title={BUYER_5MIL_HELP}>#{b.buyer_uid}</span>
                    <BuyerEmailChip email={b.buyer_email} source={b.buyer_email_source} />
                    {where && (
                      <span className="bmp-buyer-loc" title="Buyer location (state · ZIP)">{where}</span>
                    )}
                    {loc && <span className="bmp-buyer-loc" title="Buyer group / country">{loc}</span>}
                    {b.distance_miles != null && (
                      <span className="bmp-distance" title="Distance from the vehicle's proximity anchor">
                        {fmtMiles(b.distance_miles)}
                      </span>
                    )}
                    {e.lost_bid && (
                      <span style={{ ...engBadgeBase, background: '#dbeafe', color: '#1d4ed8' }}
                        title="Bid on this VIN but lost / expired — a re-engagement target">
                        Lost bid — re-engage
                      </span>
                    )}
                    {e.already_bidding && (
                      <span style={{ ...engBadgeBase, background: '#fee2e2', color: '#b91c1c' }}
                        title="Has an active bid on this VIN">
                        ● Bidding now{e.bid_count ? ` (${e.bid_count})` : ''}
                      </span>
                    )}
                    {!e.lost_bid && !e.already_bidding && e.made_offer && (
                      <span style={{ ...engBadgeBase, background: '#fef3c7', color: '#a16207' }}
                        title="Made an offer on this VIN. Ranked ABOVE a plain bid — offers convert at 31.6% vs 29.6% for bid-only. See the Fit score for the exact points.">
                        Made offer
                      </span>
                    )}
                    {e.viewed_or_saved && (
                      <span style={{ ...engBadgeBase, background: '#f1f5f9', color: '#475569' }}
                        title="Viewed the VDP or saved this VIN to a workbook">
                        {e.workbook_saved ? 'Saved' : 'Viewed'}{e.vdp_views ? ` · ${e.vdp_views}` : ''}
                      </span>
                    )}
                    {onAddToBasket && (
                      <BasketToggle
                        inBasket={inBasket}
                        // Carries the resolved ZIP/state so the basket shows where
                        // this buyer is, same as a recommended one (§2.4).
                        onAdd={() => onAddToBasket(toBasketBuyer(b), vin)}
                        onRemove={onRemoveFromBasket ? () => onRemoveFromBasket(b.buyer_uid, vin) : undefined}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        );
      })()}

      {onAddToBasket && (
        <ManualBuyerAssign vin={vin} onAssign={onAddToBasket} onUnassign={onRemoveFromBasket} basketKeys={basketKeys} />
      )}
    </section>
  );
};

/**
 * Assign a VIN to ANY buyer, not just its recommended pool. The cohort engine
 * can't know about a relationship the broker does (a standing order, a buyer
 * expanding into a new segment), so this is the manual override.
 */
const ManualBuyerAssign: React.FC<{
  vin: string;
  onAssign: (buyer: BrokerBuyerMatch, vin: string) => void;
  onUnassign?: (buyerUid: string, vin: string) => void;
  basketKeys?: Set<string>;
}> = ({ vin, onAssign, onUnassign, basketKeys }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<BrokerBuyerSearchResult[]>([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        setResults(await siftApiClient.searchBrokerBuyers(query.trim() || undefined, 15, controller.signal, vin));
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => { clearTimeout(t); controller.abort(); };
  }, [open, query, vin]);

  if (!open) {
    return (
      <div className="bmp-manual bmp-manual--closed">
        <div className="bmp-engaged-title">Someone else in mind?</div>
        <div className="bmp-manual-help">
          The list above is this vehicle's cohort matches. Search every buyer to stage
          it for someone the model would not have picked.
        </div>
        <button
          type="button"
          className="bmp-manual-open"
          onClick={() => setOpen(true)}
        >
          Search all buyers
        </button>
      </div>
    );
  }

  return (
    <div className="bmp-manual">
      <div className="bmp-engaged-title">Search all buyers</div>
      <div className="bmp-manual-help">
        Staging from here is a manual override — it does not change how this vehicle is
        scored, and the buyer keeps their place in every other list.
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by 5mil or name…"
          style={{ flex: 1, fontSize: 12, padding: '4px 8px', border: '1px solid #cbd5e1', borderRadius: 6 }}
        />
        <button
          type="button"
          onClick={() => { setOpen(false); setQuery(''); }}
          style={{ background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 12 }}
        >Cancel</button>
      </div>
      {searching && <div className="bmp-state" style={{ fontSize: 12 }}>Searching…</div>}
      {!searching && results.length === 0 && (
        <div className="bmp-state" style={{ fontSize: 12 }}>No buyers match that search.</div>
      )}
      {results.map((r) => {
        const added = !!basketKeys?.has(r.buyer_uid);
        return (
          <div key={r.buyer_uid} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '3px 0' }}>
            <span style={{ fontSize: 12, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {r.buyer_name || `Buyer ${r.buyer_uid}`}
              <span style={{ color: '#64748b' }} title={BUYER_5MIL_HELP}> · #{r.buyer_uid}</span>
              {r.buyer_parent_name && (
                <span style={{ color: '#64748b' }} title="Parent group"> · {r.buyer_parent_name}</span>
              )}
              {r.state && <span style={{ color: '#64748b' }}> · {r.state}</span>}
              {r.distance_miles != null && (
                <span
                  style={{ color: '#64748b' }}
                  title="Distance from the vehicle's proximity anchor"
                > · {fmtMiles(r.distance_miles)}</span>
              )}
              {r.total_buys != null && (
                <span style={{ color: '#64748b' }} title="Vehicles bought in the history window">
                  {' '}· {fmtInt(r.total_buys)} buys
                </span>
              )}
            </span>
            <BasketToggle
              inBasket={added}
              // Manual pick: no cohort match exists, so the fit fields stay null.
              // The location IS known though (§2.4), so carry it into the basket.
              onAdd={() => onAssign(
                {
                  buyer_uid: r.buyer_uid,
                  buyer_name: r.buyer_name ?? null,
                  buyer_zip_code: r.buyer_zip_code ?? null,
                  state: r.state ?? null,
                  distance_miles: r.distance_miles ?? null,
                } as BrokerBuyerMatch,
                vin,
              )}
              onRemove={onUnassign ? () => onUnassign(r.buyer_uid, vin) : undefined}
              className="bmp-manual-add"
              addedClassName="bmp-manual-add--added"
              addLabel="+ Add"
              addedLabel="✓ Added"
              iconOnly={false}
              style={{
                flexShrink: 0, fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 6,
                border: `1px solid ${added ? '#16a34a' : '#bfdbfe'}`,
                background: added ? '#fff' : '#eff6ff',
                color: added ? '#16a34a' : '#1d4ed8',
                cursor: added && !onUnassign ? 'default' : 'pointer',
              }}
            />
          </div>
        );
      })}
    </div>
  );
};
